/**
 * HUB-C — Carte paiement SumUp (DS Remobiles).
 */
import React from 'react';
import { CreditCard, FileText, CheckCircle, Ban } from 'lucide-react';
import {
  formatPaymentDate,
  getSumupOrderLabel,
  sumupStatusMeta,
} from '../../utils/paiements/paiementsDisplay';

export default function ActivitePaiementCard({
  payment,
  actions,
  confirming,
  cancelling,
  testIdPrefix = 'activite-paiements',
  compact = false,
}) {
  const badge = sumupStatusMeta(payment.status);
  const canConfirm = payment.status === 'PAID_PENDING';
  const canCancel = ['PAID_PENDING', 'PENDING'].includes(payment.status);

  return (
    <article
      className={`rounded-xl border ${compact ? 'p-3' : 'p-4'}`}
      style={{ borderColor: 'var(--rm-border-subtle)', background: 'var(--rm-bg-panel)' }}
      data-testid={`${testIdPrefix}-sumup-card-${payment.checkout_id}`}
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <CreditCard className="w-4 h-4 text-[var(--rm-gray-400)] shrink-0" />
            <span className="font-semibold text-[var(--rm-white)]">{getSumupOrderLabel(payment)}</span>
            <span className={badge.badge}>{badge.label}</span>
            <span className="rm-badge rm-badge--muted">SumUp</span>
          </div>
          <div className={`grid gap-2 text-sm ${compact ? 'grid-cols-1' : 'grid-cols-2 md:grid-cols-4'}`}>
            <div>
              <span className="text-[var(--rm-gray-500)]">Montant : </span>
              <span className="font-bold text-[var(--rm-white)]">{Number(payment.amount || 0).toFixed(2)} €</span>
            </div>
            <div>
              <span className="text-[var(--rm-gray-500)]">Réf : </span>
              <span className="text-[var(--rm-gray-300)] font-mono text-xs">{payment.checkout_id?.slice(0, 14)}…</span>
            </div>
            <div>
              <span className="text-[var(--rm-gray-500)]">Date : </span>
              <span className="text-[var(--rm-gray-300)]">{formatPaymentDate(payment.created_at)}</span>
            </div>
            {payment.confirmed_by && (
              <div>
                <span className="text-[var(--rm-gray-500)]">Par : </span>
                <span className="text-[var(--rm-gray-300)]">{payment.confirmed_by}</span>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => actions.openTicket(payment.checkout_id)}
            className="btn-secondary p-2.5 min-h-[44px] min-w-[44px] inline-flex items-center justify-center"
            title="Ticket PDF"
            data-testid={`${testIdPrefix}-ticket-${payment.checkout_id}`}
          >
            <FileText className="w-5 h-5" />
          </button>
          {canConfirm && (
            <button
              type="button"
              onClick={() => actions.confirmPayment(payment.checkout_id)}
              disabled={confirming === payment.checkout_id}
              className="btn-success px-4 py-2.5 min-h-[56px] text-sm inline-flex items-center gap-2"
              data-testid={`${testIdPrefix}-confirm-${payment.checkout_id}`}
            >
              <CheckCircle className="w-4 h-4" />
              Confirmer
            </button>
          )}
          {canCancel && (
            <button
              type="button"
              onClick={() => actions.cancelPayment(payment.checkout_id)}
              disabled={cancelling === payment.checkout_id}
              className="px-4 py-2.5 min-h-[56px] text-sm inline-flex items-center gap-2 rounded-xl font-semibold border"
              style={{ borderColor: 'var(--rm-border-strong)', background: 'var(--rm-red-muted)', color: 'var(--rm-red-light)' }}
              data-testid={`${testIdPrefix}-cancel-${payment.checkout_id}`}
            >
              <Ban className="w-4 h-4" />
              Annuler
            </button>
          )}
        </div>
      </div>
    </article>
  );
}
